import { Injectable } from '@angular/core';
import { ErrorHandlerService } from './error-handler.service';
import { environment } from '../../environments/environment';
import * as Parse from 'parse';

type LocalApiTarget = 'dev' | 'local';

const LOCAL_API_STORAGE_KEY = 'vesta.api';

interface IVestaBackend {
    url: string;
    appId: string;
    title: string;
}

@Injectable()
export class AppConfigService {
    #appVersion: string = '1.0.0';
    #inPIMode: boolean = false;
    #parseConfig: Parse.Config | undefined;

    #localServer: IVestaBackend = {
        url: 'http://localhost:1337/api',
        // url: 'http://localhost:3000/api',
        appId: 'APP-ID',
        // appId: 'vesta',
        title: 'Team Vesta'
    };

    #devServer: IVestaBackend = {
        url: 'https://dev.api.vesta.app/api',
        appId: 'APP-ID',
        title: 'Team Vesta'
    };

    #prodServer: IVestaBackend = {
        url: 'https://api.vesta.app/api',
        appId: 'VESTA-BLACK-WIDOW-PROD',
        title: 'Team Vesta'
    };

    constructor(private errorHandler: ErrorHandlerService) {
    }

    get appVersion() {
        return this.#appVersion;
    }

    get inPIMode() {
        return this.#inPIMode;
    }

    get parseConfig() {
        return this.#parseConfig;
    }

    get server(): IVestaBackend {
        const host = window.location.host;
        const hostname = window.location.hostname;

        if (host === 'dev.vesta.app') {
            return this.#devServer;
        }

        if (host === 'vesta.app' || host === 'www.vesta.app' || host === 'new.vesta.app') {
            return this.#prodServer;
        }

        // Capacitor WebView is https://localhost (no port). ng serve is localhost:4200 / :4201.
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
            if (environment.production) {
                return this.#prodServer;
            }
            return this.localOrDevBackend();
        }

        return environment.production ? this.#prodServer : this.localOrDevBackend();
    }

    /** DEV API unless `?api=local` / localStorage `vesta.api=local`, or legacy `localhost:4201`. */
    private localOrDevBackend(): IVestaBackend {
        return this.localApiTarget() === 'local' ? this.#localServer : this.#devServer;
    }

    private localApiTarget(): LocalApiTarget {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get('api');
        if (fromQuery === 'local' || fromQuery === 'dev') {
            try {
                localStorage.setItem(LOCAL_API_STORAGE_KEY, fromQuery);
            } catch {
                // ignore quota / private mode
            }
            return fromQuery;
        }

        try {
            const stored = localStorage.getItem(LOCAL_API_STORAGE_KEY);
            if (stored === 'local' || stored === 'dev') {
                return stored;
            }
        } catch {
            // ignore
        }

        if (window.location.host === 'localhost:4201') {
            return 'local';
        }

        return 'dev';
    }

    async fetchParseConfig() {
        try {
            console.log('Fetching Parse Config...');
            this.#parseConfig = await Parse.Config.get();
        } catch(error: any) {
            console.error(error);
            this.errorHandler.handleInvalidSessionError(error);
        }
    }
}
